import { useEffect, useMemo, useState } from 'react';
import { api, ErroApi } from '../api';
import { numero, paraCampo, paraNumero, plural, sinal } from '../formato';
import { Plano, Serie, useJanela } from '../Grafico';
import type { Ponto } from '../Grafico';
import {
  Aviso, Campo, CampoNumerico, Dialogo, ErroDaApi, EscolhaPorCodigo,
} from '../ui';
import type { OpcaoDeCodigo } from '../ui';

/**
 * A curva do picnômetro na tela (onda 322, RN-259).
 *
 * ── O que a Testesolo faz com ela ──────────────────────────────────────
 *
 * O certificado do picnômetro traz sete, oito pontos de «massa cheia de
 * água × temperatura» e, impresso, um polinômio que o laboratório que
 * calibrou ajustou à sua maneira. O LabManager NÃO usa esse polinômio:
 * ele ajusta uma parábola de mínimos quadrados aos pontos medidos, no
 * banco, e é ela que dá M₁ em cada ensaio — dentro da faixa dos pontos e
 * nunca fora dela. O polinômio impresso fica como registro, e a tela só o
 * lê para dizer se ele discorda da parábola.
 *
 * ── Por que a prévia chama a API ───────────────────────────────────────
 *
 * A conta é a MESMA função do banco que o gatilho usa ao gravar. Uma
 * segunda implementação no navegador um dia mostraria uma curva e
 * gravaria outra — a pessoa assina o que vê.
 */

/** O ajuste como a API devolve (`POST /verificacoes/curvas/ajuste`). */
export interface AjusteDaCurva {
  a0: number; a1: number; a2: number;
  r2: number; residuoMax: number;
  xMin: number; xMax: number;
  residuos: number[];
}

/** A curva como o certificado e a ficha do equipamento a trazem. */
export interface CurvaDoCertificado {
  id: string;
  equipamento_id: string;
  codigo_patrimonial: string;
  grandeza: string;
  descricao: string | null;
  funcao_texto: string | null;
  r2: number | string | null;
  observacao: string | null;
  ajuste_grau: number | null;
  ajuste_coeficientes: (number | string)[] | null;
  ajuste_r2: number | string | null;
  ajuste_residuo_max: number | string | null;
  ajuste_x_min: number | string | null;
  ajuste_x_max: number | string | null;
  pontos: { x: number | string; y: number | string }[];
}

type Coeficientes = [number, number, number];

/** Diferença acima da qual o polinômio impresso «discorda» da parábola (g). */
export const LIMITE_DE_DIVERGENCIA = 0.01;

/** Temperatura de referência da NBR 17212, onde se confere a diferença. */
const T_DE_REFERENCIA = 25;

// ── O polinômio impresso no certificado ────────────────────────────────

/**
 * Lê «y = -0,0026x2 + 0,0028x + 657,63» (vírgula decimal; «x2», «x²» ou
 * «x^2»). Devolve [a₀, a₁, a₂] — ou `null` quando o texto não é desta
 * forma: quem não lê não avisa, porque aviso por suposição é pior que
 * nenhum.
 */
export function lerPolinomio(texto: string | null | undefined): Coeficientes | null {
  if (!texto) return null;
  const limpo = texto.toLowerCase()
    .replace(/[−–—]/g, '-')
    .replace(/²/g, '2')
    .replace(/\s+/g, '')
    .replace(/,/g, '.');
  const num = '(?:\\d+\\.?\\d*|\\.\\d+)(?:e[+-]?\\d+)?';
  const re = new RegExp(
    `^(?:[a-z]\\w*=)?([+-]?${num})\\*?x\\^?2([+-]${num})\\*?x([+-]${num})$`);
  const m = limpo.match(re);
  if (!m) return null;
  const [a2, a1, a0] = [Number(m[1]), Number(m[2]), Number(m[3])];
  return [a0, a1, a2].every(Number.isFinite) ? [a0, a1, a2] : null;
}

const valorDe = (c: Coeficientes, x: number) => c[0] + c[1] * x + c[2] * x * x;

/**
 * O quanto o polinômio impresso se afasta da parábola ajustada: o maior
 * |diferença| em 50 pontos da faixa (extremos incluídos), e onde ele
 * ocorre. Mais a diferença em 25 °C quando a faixa a alcança.
 */
export function divergenciaDoCertificado(
  impresso: Coeficientes, ajustado: Coeficientes, xMin: number, xMax: number,
) {
  let max = 0;
  let em = xMin;
  const N = 50;
  for (let i = 0; i < N; i += 1) {
    const x = xMin + ((xMax - xMin) * i) / (N - 1);
    const d = Math.abs(valorDe(impresso, x) - valorDe(ajustado, x));
    if (d > max) { max = d; em = x; }
  }
  const naReferencia = T_DE_REFERENCIA >= xMin && T_DE_REFERENCIA <= xMax
    ? Math.abs(valorDe(impresso, T_DE_REFERENCIA) - valorDe(ajustado, T_DE_REFERENCIA))
    : null;
  return { max, em, naReferencia };
}

// ── O que se escreve da curva ──────────────────────────────────────────

/** Coeficiente com o sinal que se lê («−0,002633»), U+2212 como na coluna de desvios. */
const coef = (v: number, casas: number) =>
  `${v < 0 ? '−' : ''}${numero(Math.abs(v), casas)}`;

/** Os números do ajuste, em duas linhas curtas: a parábola e a qualidade dela. */
export function ResumoDoAjuste({ a, r2, residuoMax, xMin, xMax }: {
  a: Coeficientes; r2: number; residuoMax: number; xMin: number; xMax: number;
}) {
  return (
    <div className="curva-resumo">
      <p className="mono">
        <span>{`a₂ = ${coef(a[2], 6)}`}</span>{' · '}
        <span>{`a₁ = ${coef(a[1], 6)}`}</span>{' · '}
        <span>{`a₀ = ${coef(a[0], 4)}`}</span>
      </p>
      <p>
        <span>{`R² = ${numero(r2, 4)}`}</span>{' · '}
        <span>{`maior resíduo ${numero(residuoMax, 3, true)} g`}</span>{' · '}
        <span>{`faixa ${numero(xMin, 4, true)} a ${numero(xMax, 4, true)} °C`}</span>
      </p>
    </div>
  );
}

/** Os coeficientes de uma curva do certificado/ficha, quando ela tem o ajuste. */
export function ajusteDaCurva(c: Partial<Pick<CurvaDoCertificado,
  'grandeza' | 'ajuste_grau' | 'ajuste_coeficientes' | 'ajuste_r2'
  | 'ajuste_residuo_max' | 'ajuste_x_min' | 'ajuste_x_max'>>) {
  // A parábola é só da massa do picnômetro cheio; as outras curvas (a do
  // densímetro, por exemplo) seguem como eram, sem resumo nem gráfico T×M₁.
  if (c.grandeza !== undefined && c.grandeza !== 'massa_com_agua') return null;
  if (c.ajuste_grau !== 2 || !c.ajuste_coeficientes || c.ajuste_coeficientes.length !== 3
    || c.ajuste_x_min == null || c.ajuste_x_max == null) return null;
  const [a0, a1, a2] = c.ajuste_coeficientes.map(Number);
  return {
    a: [a0, a1, a2] as Coeficientes,
    r2: Number(c.ajuste_r2),
    residuoMax: Number(c.ajuste_residuo_max),
    xMin: Number(c.ajuste_x_min),
    xMax: Number(c.ajuste_x_max),
  };
}

// ── O gráfico ──────────────────────────────────────────────────────────

/**
 * Os pontos medidos, a parábola ajustada (só de x_min a x_max: nunca
 * extrapola) e, tracejado, o polinômio impresso — quando há.
 */
export function GraficoDaCurva({ pontos, a, xMin, xMax, impresso }: {
  pontos: Ponto[];
  a?: Coeficientes | null;
  xMin?: number; xMax?: number;
  impresso?: Coeficientes | null;
}) {
  const pontosDe = (c: Coeficientes | null | undefined): Ponto[] => {
    if (!c || xMin == null || xMax == null) return [];
    return Array.from({ length: 41 }, (_, i) => {
      const x = xMin + ((xMax - xMin) * i) / 40;
      return { x, y: valorDe(c, x) };
    });
  };
  const curva = useMemo(() => pontosDe(a), [a, xMin, xMax]);
  const tracejada = useMemo(() => pontosDe(impresso), [impresso, xMin, xMax]);
  const todos = useMemo(() => [...pontos, ...curva, ...tracejada],
    [pontos, curva, tracejada]);
  const z = useJanela(todos);

  return (
    <div className="curva-grafico">
      <Plano janela={z.janela} tituloX="Temperatura, T (°C)" tituloY="M₁ (g)"
        desabilitado>
        {({ px }) => (
          <>
            {tracejada.length > 0 && (
              <path className="serie certificado" d={tracejada.map((p, i) => {
                const q = px(p);
                return `${i ? 'L' : 'M'}${q.x.toFixed(2)} ${q.y.toFixed(2)}`;
              }).join(' ')} />
            )}
            {curva.length > 0 && <Serie pontos={curva} px={px} marcar={false} />}
            {pontos.map((p, i) => {
              const q = px(p);
              return <circle key={i} className="ponto" cx={q.x} cy={q.y} r={4} />;
            })}
          </>
        )}
      </Plano>
      <div className="fe-legenda">
        <span><i className="l-sed" />pontos do certificado</span>
        {curva.length > 0 && <span><i className="l-reta" />parábola ajustada</span>}
        {tracejada.length > 0 && <span><i className="l-aux" />polinômio impresso</span>}
      </div>
    </div>
  );
}

// ── O diálogo ──────────────────────────────────────────────────────────

interface Linha { x: string; y: string }
const linhaVazia = (): Linha => ({ x: '', y: '' });

/**
 * Cola a tabela da planilha: linhas separadas por quebra, colunas por
 * tabulação (ou «;»). A primeira linha que não for número — um cabeçalho —
 * fica de fora.
 */
function lerColagem(texto: string): string[][] {
  return texto.split(/\r?\n/)
    .map((l) => l.split(/\t|;/).map((c) => c.trim()))
    .filter((cs) => cs.some((c) => c !== '') && cs.some((c) => paraNumero(c) !== undefined));
}

export function FormCurva({ certificadoId, curva, aoFechar }: {
  certificadoId: string;
  /** Presente = editar a curva existente. */
  curva?: CurvaDoCertificado;
  aoFechar(): void;
}) {
  const [picnometros, setPicnometros] = useState<OpcaoDeCodigo[]>([]);
  const [equipamentoId, setEquipamentoId] = useState(curva?.equipamento_id ?? '');
  const [descricao, setDescricao] = useState(
    curva?.descricao ?? 'Massa do picnômetro cheio de água por temperatura');
  const [linhas, setLinhas] = useState<Linha[]>(() =>
    curva?.pontos.length
      ? curva.pontos.map((p) => ({ x: paraCampo(p.x), y: paraCampo(p.y) }))
      : [linhaVazia(), linhaVazia(), linhaVazia(), linhaVazia()]);
  const [funcaoTexto, setFuncaoTexto] = useState(curva?.funcao_texto ?? '');
  const [r2Texto, setR2Texto] = useState(curva?.r2 != null ? paraCampo(curva.r2) : '');
  const [erro, setErro] = useState<unknown>(null);
  const [indo, setIndo] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const e = await api<{ id: string; codigo_patrimonial: string; nome: string;
          status?: string | null; baixa_tipo?: string | null }[]>(
          '/equipamentos?categoria=picnometro&limite=500');
        /* Só o que pode receber curva: sem baixa e não inativo. */
        setPicnometros((Array.isArray(e) ? e : [])
          .filter((x) => !x.baixa_tipo && !['inativo', 'descartado'].includes(x.status ?? ''))
          .map((x) => (
          { valor: x.id, codigo: x.codigo_patrimonial, detalhe: x.nome })));
      } catch { /* o seletor fica vazio e a tela diz isso */ }
    })();
  }, []);

  const mexer = (i: number, k: keyof Linha, v: string) =>
    setLinhas((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));

  /** O Ctrl+V da planilha: preenche a partir do campo onde caiu. */
  const colar = (e: React.ClipboardEvent<HTMLInputElement>, i: number, k: keyof Linha) => {
    const texto = e.clipboardData.getData('text');
    if (!/[\t\n;]/.test(texto)) return;
    const colados = lerColagem(texto);
    if (!colados.length) return;
    e.preventDefault();
    setLinhas((ls) => {
      const novas = ls.slice();
      colados.forEach((cs, n) => {
        const alvo = i + n;
        while (novas.length <= alvo) novas.push(linhaVazia());
        const l = { ...novas[alvo] };
        if (k === 'x') { l.x = cs[0] ?? ''; if (cs.length > 1) l.y = cs[1]; }
        else l.y = cs[0] ?? '';
        novas[alvo] = l;
      });
      return novas;
    });
  };

  // Só as linhas completas entram na conta; a ordem delas é a dos resíduos.
  const validas = useMemo(() => linhas.flatMap((l, i) => {
    const x = paraNumero(l.x);
    const y = paraNumero(l.y);
    return x !== undefined && y !== undefined ? [{ i, x, y }] : [];
  }), [linhas]);
  const pontos = useMemo(() => validas.map(({ x, y }) => ({ x, y })), [validas]);
  const repetidos = new Set(pontos.map((p) => p.x)).size !== pontos.length;

  // A prévia: a mesma função do banco, 300 ms depois da última mexida.
  /* A prévia leva a chave dos pontos de que saiu: número de uma tabela
     velha nunca aparece, e «Gravar» espera o ajuste da tabela de agora. */
  const [previa, setPrevia] = useState<
    { chave: string; ajuste: AjusteDaCurva | null; erro: string | null }>(
    { chave: '', ajuste: null, erro: null });
  const chave = JSON.stringify(pontos);
  useEffect(() => {
    if (pontos.length < 4 || repetidos) {
      setPrevia({ chave, ajuste: null, erro: null });
      return undefined;
    }
    let vivo = true;
    const t = setTimeout(() => {
      api<AjusteDaCurva>('/verificacoes/curvas/ajuste', { corpo: { pontos } })
        .then((a) => { if (vivo) setPrevia({ chave, ajuste: a, erro: null }); })
        .catch((e) => {
          if (vivo) {
            setPrevia({ chave, ajuste: null,
              erro: e instanceof ErroApi ? e.message : 'Não consegui calcular o ajuste.' });
          }
        });
    }, 300);
    return () => { vivo = false; clearTimeout(t); };
    // `chave` é o conteúdo de `pontos`: refazer só quando os números mudam.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave, repetidos]);

  const atual = previa.chave === chave;
  const aj = atual ? previa.ajuste : null;
  const a: Coeficientes | null = aj ? [aj.a0, aj.a1, aj.a2] : null;
  const impresso = lerPolinomio(funcaoTexto);
  const divergencia = a && impresso && aj
    ? divergenciaDoCertificado(impresso, a, aj.xMin, aj.xMax) : null;

  const r2 = paraNumero(r2Texto);
  const falta = !equipamentoId ? 'Escolha o picnômetro.'
    : !descricao.trim() ? 'Descreva a curva.'
      : repetidos ? 'Cada temperatura só pode aparecer uma vez.'
        : pontos.length < 4
          ? `${4 - pontos.length === 1 ? 'Falta' : 'Faltam'} ${plural(4 - pontos.length, 'ponto', 'pontos')}: a parábola precisa de pelo menos 4.`
          : r2Texto && (r2 === undefined || r2 < 0 || r2 > 1)
            ? 'O R² impresso no certificado tem de ser um número entre 0 e 1.'
            : !aj ? 'Aguarde o cálculo do ajuste.' : null;

  const enviar = async () => {
    setIndo(true); setErro(null);
    try {
      await api(`/verificacoes/certificados/${certificadoId}/curvas`, {
        metodo: 'PUT',
        corpo: {
          equipamentoId, grandeza: 'massa_com_agua', descricao: descricao.trim(),
          variavelX: 'temperatura', unidadeX: '°C', unidadeY: 'g',
          funcaoTexto: funcaoTexto.trim() || undefined,
          r2Certificado: r2,
          observacao: curva?.observacao ?? undefined,
          pontos,
        },
      });
      aoFechar();
    } catch (e) { setErro(e); setIndo(false); }
  };

  return (
    <Dialogo aberto larga titulo="Curva do picnômetro"
      rotulo={curva ? `Editar · ${curva.codigo_patrimonial}` : 'Certificado em rascunho'}
      aoFechar={aoFechar}>
      <p className="sub">
        Os pontos do certificado: a massa do picnômetro cheio de água em cada
        temperatura. O sistema ajusta uma parábola de mínimos quadrados a eles
        e só a usa da menor à maior temperatura dos pontos — fora dessa faixa
        não há medida. Cole a tabela direto da planilha (temperatura e
        massa, separadas por tabulação).
      </p>

      <EscolhaPorCodigo rotulo="Picnômetro" opcoes={picnometros}
        valor={equipamentoId} aoEscolher={setEquipamentoId}
        desabilitado={curva !== undefined}
        dica={curva ? 'Para trocar de picnômetro, retire a curva e crie outra.' : undefined} />
      <Campo rotulo="Descrição" maxLength={200} value={descricao}
        onChange={(e) => setDescricao(e.target.value)} />

      <div className="rola">
        <table className="lista compacta curva-tabela">
          <thead>
            <tr>
              <th className="num">Nº</th>
              <th className="num">T (°C)</th>
              <th className="num">M₁ (g)</th>
              <th className="num">Resíduo (g)</th>
              <th><span className="so-leitor">Remover</span></th>
            </tr>
          </thead>
          <tbody>
            {linhas.map((l, i) => {
              const k = validas.findIndex((v) => v.i === i);
              return (
                <tr key={i}>
                  <td className="num">{i + 1}</td>
                  <td>
                    <CampoNumerico rotulo={`Temperatura da linha ${i + 1} (°C)`} rotuloOculto
                      value={l.x} onChange={(e) => mexer(i, 'x', e.target.value)}
                      onPaste={(e) => colar(e, i, 'x')} />
                  </td>
                  <td>
                    <CampoNumerico rotulo={`Massa da linha ${i + 1} (g)`} rotuloOculto
                      value={l.y} onChange={(e) => mexer(i, 'y', e.target.value)}
                      onPaste={(e) => colar(e, i, 'y')} />
                  </td>
                  <td className="num mono">
                    {aj && k >= 0 && aj.residuos[k] !== undefined
                      ? sinal(aj.residuos[k], 3) : <span className="nada">—</span>}
                  </td>
                  <td>
                    <button type="button" className="botao discreto pequeno"
                      aria-label={`Remover a linha ${i + 1}`}
                      disabled={linhas.length <= 1}
                      onClick={() => setLinhas((ls) => ls.filter((_, j) => j !== i))}>
                      ×
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="acoes">
        <button type="button" className="botao discreto pequeno"
          onClick={() => setLinhas((ls) => [...ls, linhaVazia()])}>
          + linha
        </button>
      </div>

      {atual && previa.erro && <Aviso tipo="erro" titulo="O ajuste não saiu">{previa.erro}</Aviso>}
      {aj && a && (
        <>
          <ResumoDoAjuste a={a} r2={aj.r2} residuoMax={aj.residuoMax}
            xMin={aj.xMin} xMax={aj.xMax} />
          <GraficoDaCurva pontos={pontos} a={a} xMin={aj.xMin} xMax={aj.xMax}
            impresso={impresso} />
        </>
      )}
      {!aj && pontos.length > 0 && (
        <GraficoDaCurva pontos={pontos} />
      )}

      <Campo rotulo="Polinômio impresso no certificado" maxLength={300}
        placeholder="y = -0,0026x2 + 0,0028x + 657,63" value={funcaoTexto}
        onChange={(e) => setFuncaoTexto(e.target.value)}
        dica="Só registro: o sistema não calcula com ele, apenas o compara com a parábola ajustada." />
      <CampoNumerico rotulo="R² impresso no certificado" value={r2Texto}
        onChange={(e) => setR2Texto(e.target.value)}
        erro={r2Texto && (r2 === undefined || r2 < 0 || r2 > 1) ? 'entre 0 e 1' : undefined} />

      {divergencia && divergencia.max > LIMITE_DE_DIVERGENCIA && (
        <Aviso tipo="atencao" titulo="O polinômio do certificado difere da parábola ajustada">
          {`A maior diferença na faixa é de ${numero(divergencia.max, 3)} g, a ${numero(divergencia.em, 1)} °C`}
          {divergencia.naReferencia !== null
            && `; a ${numero(T_DE_REFERENCIA, 0)} °C é de ${numero(divergencia.naReferencia, 3)} g`}
          {`. Acima de ${numero(LIMITE_DE_DIVERGENCIA, 2)} g vale conferir os pontos digitados
          contra o papel: o sistema usa a parábola dos pontos, não o polinômio impresso.`}
        </Aviso>
      )}

      {falta && <p className="sub">{falta}</p>}
      {erro ? <ErroDaApi erro={erro} /> : null}
      <div className="rodape-dialogo">
        <button type="button" className="botao secundario" onClick={aoFechar}>
          Cancelar
        </button>
        <button type="button" className="botao" disabled={indo || falta !== null}
          onClick={() => void enviar()}>
          {indo ? 'Gravando…' : 'Gravar'}
        </button>
      </div>
    </Dialogo>
  );
}

/** Retirar a curva de um certificado em rascunho — com confirmação. */
export function FormRetirarCurva({ certificadoId, curva, aoFechar }: {
  certificadoId: string; curva: CurvaDoCertificado; aoFechar(): void;
}) {
  const [erro, setErro] = useState<unknown>(null);
  const [indo, setIndo] = useState(false);
  const retirar = async () => {
    setIndo(true); setErro(null);
    try {
      await api(`/verificacoes/certificados/${certificadoId}/curvas/${curva.id}`,
        { metodo: 'DELETE' });
      aoFechar();
    } catch (e) { setErro(e); setIndo(false); }
  };
  return (
    <Dialogo aberto titulo="Retirar a curva" rotulo={curva.codigo_patrimonial}
      aoFechar={aoFechar}>
      <p>
        A curva de {curva.codigo_patrimonial} sai deste certificado em
        rascunho, com os {curva.pontos.length} pontos digitados. Nada foi
        publicado ainda; é só digitar de novo se for engano.
      </p>
      {erro ? <ErroDaApi erro={erro} /> : null}
      <div className="rodape-dialogo">
        <button type="button" className="botao secundario" onClick={aoFechar}>
          Cancelar
        </button>
        <button type="button" className="botao perigo" disabled={indo}
          onClick={() => void retirar()}>
          {indo ? 'Retirando…' : 'Retirar a curva'}
        </button>
      </div>
    </Dialogo>
  );
}
